// src/pages/Dashboard.js - UPDATED WITH ACCESS DENIED HANDLING
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';

// Simple Modal Component built directly into Dashboard
const SimpleModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        
        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// Function to generate a better display ID
const generateDisplayId = (firebaseId, index) => {
  // If the Firebase ID contains underscores, extract the meaningful parts
  if (firebaseId && firebaseId.includes('_')) {
    const parts = firebaseId.split('_');
    // Use the timestamp part (1760552865292) and the random part (k28xghy4v)
    if (parts.length >= 3) {
      const timestamp = parts[1]; // 1760552865292
      const randomPart = parts[2]; // k28xghy4v
      // Use last 6 chars of timestamp and first 4 chars of random part
      return `SH${timestamp.slice(-6)}${randomPart.substring(0, 4).toUpperCase()}`;
    }
  }
  
  // If no underscores or unexpected format, generate sequential ID
  return `SH${String(index + 1).padStart(6, '0')}`;
};

const Dashboard = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBOLModalOpen, setIsBOLModalOpen] = useState(false);
  const [selectedBOLUrl, setSelectedBOLUrl] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inTransit: 0,
    delivered: 0
  });
  const [viewMode, setViewMode] = useState('recent'); // 'recent' or 'all'
  
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // Check for access denied message
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');

  useEffect(() => {
    if (location.state?.message) {
      setAccessDeniedMessage(location.state.message);
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (!currentUser) return;

    // ✅ CRITICAL FIX: Only fetch shipments that should be shown in dashboard
    const shipmentsQuery = query(
      collection(db, 'shipments'),
      where('userId', '==', currentUser.uid),
      where('shouldShowInDashboard', '==', true), // ← ADD THIS FILTER
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(shipmentsQuery, (querySnapshot) => {
      const shipmentsData = [];
      let pendingCount = 0;
      let inTransitCount = 0;
      let deliveredCount = 0;

      querySnapshot.forEach((doc, index) => {
        const shipment = { 
          id: doc.id, 
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          // Generate a proper display ID
          displayId: generateDisplayId(doc.id, index)
        };
        shipmentsData.push(shipment);

        // Count statuses
        if (shipment.status === 'pending' || shipment.status === 'pending_payment' || shipment.status === 'pending_verification') {
          pendingCount++;
        } else if (shipment.status === 'in_transit') {
          inTransitCount++;
        } else if (shipment.status === 'delivered') {
          deliveredCount++;
        }
      });

      setShipments(shipmentsData);
      setStats({
        total: shipmentsData.length,
        pending: pendingCount,
        inTransit: inTransitCount,
        delivered: deliveredCount
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleViewDetails = (shipment) => {
    setSelectedShipment(shipment);
    setIsModalOpen(true);
  };

  const handleViewBOL = (bolUrl) => {
    setSelectedBOLUrl(bolUrl);
    setIsBOLModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedShipment(null);
  };

  const handleCloseBOLModal = () => {
    setIsBOLModalOpen(false);
    setSelectedBOLUrl('');
  };

  const canViewBOL = (shipment) => {
    // Customer can view BOL only when:
    // 1. Shipment is delivered AND
    // 2. BOL URL exists AND
    // 3. Either customerCanViewBOL is true OR signatures exist
    return shipment.status === 'delivered' && 
           shipment.bolUrl && 
           (shipment.customerCanViewBOL || 
            shipment.signatures?.delivery?.signature || 
            shipment.signatures?.pickup?.signature);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      pending_payment: { color: 'bg-orange-100 text-orange-800', label: 'Pending Payment' },
      pending_verification: { color: 'bg-orange-100 text-orange-800', label: 'Verification Pending' },
      awaiting_payment: { color: 'bg-orange-100 text-orange-800', label: 'Awaiting Payment' },
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
      confirmed: { color: 'bg-blue-100 text-blue-800', label: 'Confirmed' },
      in_transit: { color: 'bg-purple-100 text-purple-800', label: 'In Transit' },
      delivered: { color: 'bg-green-100 text-green-800', label: 'Delivered' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get shipments based on view mode
  const displayShipments = viewMode === 'recent' ? shipments.slice(0, 10) : shipments;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading your shipments..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Welcome back, {currentUser?.displayName || currentUser?.email || 'User'}! Here's your shipping overview.
          </p>
        </div>

        {/* Access Denied Message */}
        {accessDeniedMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Access Denied
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{accessDeniedMessage}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Total Shipments</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Pending</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">In Transit</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.inTransit}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Delivered</h3>
                <p className="text-2xl font-bold text-gray-900">{stats.delivered}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8 flex flex-wrap gap-4 items-center">
          <Link
            to="/shipping-form"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Book Shipment
          </Link>
          
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('recent')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                viewMode === 'recent'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Recent Shipments
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                viewMode === 'all'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Shipments ({shipments.length})
            </button>
          </div>
        </div>

        {/* Shipments Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {viewMode === 'recent' ? 'Recent Shipments' : 'All Shipments'}
            </h2>
            {viewMode === 'recent' && shipments.length > 10 && (
              <span className="text-sm text-gray-500">
                Showing 10 of {shipments.length} shipments
              </span>
            )}
          </div>
          <div className="p-6">
            {shipments.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No shipments</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating a new shipment.</p>
                <div className="mt-6">
                  <Link
                    to="/shipping-form"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Create Your First Shipment
                  </Link>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Shipment ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayShipments.map((shipment) => (
                      <tr key={shipment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 font-mono">
                            {shipment.displayId}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {shipment.itemDescription}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(shipment.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${shipment.agreedPrice}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(shipment.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(shipment)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            View Details
                          </button>
                          {canViewBOL(shipment) && (
                            <button
                              onClick={() => handleViewBOL(shipment.bolUrl)}
                              className="text-green-600 hover:text-green-900"
                            >
                              View Signed BOL
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Shipment Details Modal */}
      <SimpleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Shipment Details"
      >
        {selectedShipment && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Shipment ID</label>
                <div className="mt-1">
                  <p className="text-sm font-mono text-gray-900 font-semibold">
                    {selectedShipment.displayId}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">{getStatusBadge(selectedShipment.status)}</div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Item Description</label>
              <p className="mt-1 text-sm text-gray-900">{selectedShipment.itemDescription}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Weight</label>
                <p className="mt-1 text-sm text-gray-900">{selectedShipment.weight} lbs</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Agreed Price</label>
                <p className="mt-1 text-sm text-gray-900">${selectedShipment.agreedPrice}</p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Pickup Address</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{selectedShipment.pickupAddress}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Delivery Address</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{selectedShipment.dropoffAddress}</p>
            </div>
            
            {selectedShipment.specialInstructions && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                <p className="mt-1 text-sm text-gray-900">{selectedShipment.specialInstructions}</p>
              </div>
            )}
            
            {/* Show BOL status */}
            {selectedShipment.bolUrl && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800">Bill of Lading</h4>
                <p className="text-sm text-blue-700 mt-1">
                  {canViewBOL(selectedShipment) 
                    ? "Your signed BOL is ready to view."
                    : "BOL is being prepared. You'll be able to view it once the shipment is delivered and signed."
                  }
                </p>
              </div>
            )}
            
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </SimpleModal>

      {/* BOL Preview Modal */}
      <SimpleModal
        isOpen={isBOLModalOpen}
        onClose={handleCloseBOLModal}
        title="Signed Bill of Lading"
      >
        <div className="h-96 sm:h-120 flex flex-col">
          <div className="flex-1 border rounded-lg">
            <iframe
              src={selectedBOLUrl}
              className="w-full h-full border-0"
              title="Signed Bill of Lading"
            />
          </div>
          <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
            <a
              href={selectedBOLUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-4 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
            >
              Download BOL
            </a>
            <button
              onClick={handleCloseBOLModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </SimpleModal>
    </div>
  );
};

export default Dashboard;